export type RouteSEO = {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogType: string;
  ogImage: string;
  canonical: string;
  keywords: string;
};

const BASE_URL = "https://0xfire.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/0xfire.png`;
const SAILFISH_OG_IMAGE = `${BASE_URL}/sailfish_web.png`;

export const ROUTE_SEO: Record<string, RouteSEO> = {
  "/": {
    title: "0xfire | Crypto Trading Infrastructure & Bot Development",
    description:
      "0xfire builds crypto trading bots and real-time market data infrastructure. Sailfish API for multi-chain streaming across Solana, Ethereum, Hyperliquid, Polymarket, and more. Trading bot mentorship, strategy development, and free DeFi tools.",
    ogTitle: "0xfire — Crypto Trading Infrastructure & Bot Development",
    ogDescription:
      "We build trading bots for a living. Real-time Solana & Polymarket data via Sailfish API, trading bot mentorship, and free DeFi tools.",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    canonical: `${BASE_URL}/`,
    keywords:
      "crypto trading bots, trading infrastructure, Solana trading, algorithmic trading, DeFi tools, 0xfire",
  },
  "/products/sailfish": {
    title: "Sailfish — Real-Time Multi-Chain Trading Data API | 0xfire",
    description:
      "Sailfish streams live trades, pool initializations, token events, and orderbooks across Solana, Ethereum, Binance Chain, Hyperliquid, Polymarket, and Kalshi. WebSocket + REST API with 20-50ms latency. Open-source TypeScript SDK. From $75/mo.",
    ogTitle: "Sailfish — Real-Time Multi-Chain Trading Data API",
    ogDescription:
      "Stream live trades and orderbooks across Solana, Ethereum, Hyperliquid, Polymarket, and more. 20-50ms latency. Open-source SDK. From $75/mo.",
    ogType: "website",
    ogImage: SAILFISH_OG_IMAGE,
    canonical: `${BASE_URL}/products/sailfish`,
    keywords:
      "Solana trading API, real-time trading data, Polymarket API, WebSocket trading, crypto data feed, Sailfish, 0xfire",
  },
  "/products/sailfish/docs": {
    title: "Sailfish API Documentation — Endpoints, Schema & SDK | 0xfire",
    description:
      "Complete Sailfish API documentation. WebSocket endpoints, REST API, data schemas (Trade, TradeRaw, PoolInfo), TypeScript SDK installation and usage guides.",
    ogTitle: "Sailfish API Docs — 0xfire",
    ogDescription:
      "WebSocket endpoints, REST API, data schemas, and TypeScript SDK documentation for the Sailfish trading data API.",
    ogType: "website",
    ogImage: SAILFISH_OG_IMAGE,
    canonical: `${BASE_URL}/products/sailfish/docs`,
    keywords:
      "Sailfish API docs, Solana WebSocket API, trading data SDK, API documentation, 0xfire",
  },
  "/products/sailfish/solana": {
    title: "Solana Live Trading Dashboard — Real-Time DEX Data | 0xfire",
    description:
      "Watch real-time Solana DEX trades streaming live. Powered by Sailfish API. Tracks Raydium, PumpFun, PumpSwap, and Meteora trades with sub-second latency.",
    ogTitle: "Solana Live Trading Dashboard — 0xfire",
    ogDescription:
      "Real-time Solana DEX trade streaming. Raydium, PumpFun, PumpSwap, Meteora. Powered by Sailfish.",
    ogType: "website",
    ogImage: SAILFISH_OG_IMAGE,
    canonical: `${BASE_URL}/products/sailfish/solana`,
    keywords:
      "Solana live trades, DEX dashboard, real-time trading, Raydium, PumpFun, Meteora, 0xfire",
  },
  "/products/sailfish/polymarket": {
    title: "Polymarket Live Orderbook — Real-Time Prediction Market Data | 0xfire",
    description:
      "Stream live Polymarket orderbooks in real-time. Powered by Sailfish API. Watch bids, asks, and spreads across active prediction markets.",
    ogTitle: "Polymarket Live Orderbook — 0xfire",
    ogDescription:
      "Real-time Polymarket orderbook streaming. Live bids, asks, and spreads. Powered by Sailfish.",
    ogType: "website",
    ogImage: SAILFISH_OG_IMAGE,
    canonical: `${BASE_URL}/products/sailfish/polymarket`,
    keywords:
      "Polymarket orderbook, prediction market data, Polymarket API, live orderbook, 0xfire",
  },
  "/services": {
    title:
      "Crypto Trading Bot Development & Mentorship Services | 0xfire",
    description:
      "Strategy review & audit ($499), trading bot mentorship ($1,599), strategy backtesting (from $2,499), and strategy co-development (from $1,299/mo). Book a free 15-minute call.",
    ogTitle: "Trading Bot Development & Mentorship — 0xfire",
    ogDescription:
      "Strategy review, bot mentorship, backtesting, and co-development. From $499. Book a free call.",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    canonical: `${BASE_URL}/services`,
    keywords:
      "crypto trading bot development, trading bot mentorship, strategy backtesting, algorithmic trading services, 0xfire",
  },
  "/tools": {
    title: "Free DeFi Tools — Solana Dashboard & Utilities | 0xfire",
    description:
      "Free crypto trading tools by 0xfire. Solana live trading dashboard, historic trade data queries, pool graduations tracker, SOL wrapper, and Polymarket orderbook viewer.",
    ogTitle: "Free DeFi Tools — 0xfire",
    ogDescription:
      "Solana live dashboard, historic trade data, pool graduations, SOL wrapper, Polymarket orderbook. All free.",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    canonical: `${BASE_URL}/tools`,
    keywords:
      "DeFi tools, Solana tools, free crypto tools, trading dashboard, SOL wrapper, 0xfire",
  },
  "/tools/solana": {
    title: "Solana Live Trading Dashboard — Real-Time DEX Data | 0xfire",
    description:
      "Watch real-time Solana DEX trades streaming live. Powered by Sailfish API. Tracks Raydium, PumpFun, PumpSwap, and Meteora trades with sub-second latency.",
    ogTitle: "Solana Live Trading Dashboard — 0xfire",
    ogDescription:
      "Real-time Solana DEX trade streaming. Raydium, PumpFun, PumpSwap, Meteora. Powered by Sailfish.",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    canonical: `${BASE_URL}/tools/solana`,
    keywords:
      "Solana live trades, DEX dashboard, real-time trading, Raydium, PumpFun, Meteora, 0xfire",
  },
  "/tools/solana/historic": {
    title: "Solana Historic Trade Data — Query & Chart Past Trades | 0xfire",
    description:
      "Query and chart historical Solana DEX trades by pool or token address. Candlestick charts with technical indicators. Powered by Sailfish historic API.",
    ogTitle: "Solana Historic Trade Data — 0xfire",
    ogDescription:
      "Query and chart historical Solana trades. Candlestick charts, technical indicators. Free tool by 0xfire.",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    canonical: `${BASE_URL}/tools/solana/historic`,
    keywords:
      "Solana trade history, historic DEX data, Solana charts, candlestick charts, 0xfire",
  },
  "/tools/solana/graduations": {
    title: "Solana Pool Graduations — PumpFun & Raydium Tracker | 0xfire",
    description:
      "Track PumpFun and Raydium LaunchLab pool graduations in real-time. See which tokens graduate from bonding curves to DEX liquidity pools on Solana.",
    ogTitle: "Solana Pool Graduations Tracker — 0xfire",
    ogDescription:
      "Track PumpFun and Raydium pool graduations in real-time. Free Solana tool by 0xfire.",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    canonical: `${BASE_URL}/tools/solana/graduations`,
    keywords:
      "PumpFun graduation, Raydium LaunchLab, Solana pool graduation, bonding curve, token launch, 0xfire",
  },
  "/tools/solana/wrap-sol": {
    title: "Wrap & Unwrap SOL to wSOL — Free Solana Tool | 0xfire",
    description:
      "Wrap and unwrap SOL to wSOL instantly. Free Solana tool — connect your Phantom or Solflare wallet and convert between SOL and Wrapped SOL in seconds. No fees, no sign-up.",
    ogTitle: "Wrap & Unwrap SOL — Free Solana Tool | 0xfire",
    ogDescription:
      "Convert between SOL and Wrapped SOL (wSOL) instantly. Connect your wallet, wrap or unwrap in one click. Free, no sign-up.",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    canonical: `${BASE_URL}/tools/solana/wrap-sol`,
    keywords:
      "wrap SOL, unwrap SOL, wSOL, wrapped SOL, Solana, SOL to wSOL, Phantom wallet, 0xfire",
  },
  "/tools/polymarket": {
    title: "Polymarket Live Orderbook — Real-Time Prediction Market Data | 0xfire",
    description:
      "Stream live Polymarket orderbooks in real-time. Watch bids, asks, and spreads across active prediction markets. Free tool powered by Sailfish.",
    ogTitle: "Polymarket Live Orderbook — 0xfire",
    ogDescription:
      "Real-time Polymarket orderbook streaming. Live bids, asks, and spreads. Free tool by 0xfire.",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    canonical: `${BASE_URL}/tools/polymarket`,
    keywords:
      "Polymarket orderbook, prediction market data, live orderbook, Polymarket trading, 0xfire",
  },
  "/workshops/dont-get-wrecked": {
    title:
      "Don't Get Wrecked In Crypto — Trading Workshop Berlin | 0xfire",
    description:
      "Weekly crypto trading workshop in Berlin. Learn trading risks, MEV bots, coin launches, financial instruments, and strategy building. Sundays 2:30 PM. EUR 65.",
    ogTitle: "Don't Get Wrecked In Crypto — Workshop Berlin",
    ogDescription:
      "Weekly crypto workshop in Berlin. Trading risks, MEV bots, strategy building. Sundays 2:30 PM. EUR 65.",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    canonical: `${BASE_URL}/workshops/dont-get-wrecked`,
    keywords:
      "crypto workshop Berlin, trading workshop, crypto education, MEV bots, trading strategy, 0xfire",
  },
};
